import { AlertType, appendAlertItem } from '@pruforce/common-adminweb-sdk';
import { Box, Button, Checkbox, CircularProgress, Typography } from '@mui/material';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { formatDateTime, formatNumber } from 'src/app/modules/OpmPd/utils/commonHelper';
import AppTable from 'src/app/common/components/AppTable/AppTable';
import OpmPdReasonFormDialog from 'src/app/common/components/OpmPdReasonFormDialog/OpmPdReasonFormDialog';
import { useTranslation } from 'src/app/common/hook/translation.hook';
import { approveLmBatchFiles, rejectLmBatchFiles, sendBackLmBatchFiles } from 'src/app/modules/OpmPd/network/opm-pd-lm';
import { alertDialog } from 'src/utils/alertDialog';
import { useLmBatchFilePruTable } from './useLmBatchFilePruTable';
import _isEqual from 'lodash/isEqual';

const initialFilters = {};
type BatchAction = 'sendBack' | 'reject' | 'approve';
type ReasonDialogAction = Extract<BatchAction, 'sendBack' | 'reject'>;

const BatchFileList: FC = () => {
  const t = useTranslation();
  const dispatch = useDispatch();
  const [selectedBatchIds, setSelectedBatchIds] = useState<string[]>([]);
  const [reasonDialogAction, setReasonDialogAction] = useState<ReasonDialogAction | null>(null);
  const [isSubmittingSendBack, setIsSubmittingSendBack] = useState(false);
  const [isSubmittingReject, setIsSubmittingReject] = useState(false);
  const [isSubmittingApprove, setIsSubmittingApprove] = useState(false);

  const { filterState, lmBatchFiles, isLoading, setFilterState, refreshData, onSort } = useLmBatchFilePruTable({
    initialParams: initialFilters,
  });

  const validBatchIds = useMemo(() => {
    return (
      lmBatchFiles?.items
        .filter((batchFile) => (batchFile.awaitingApprovalCount || 0) > 0)
        .map((batchFile) => String(batchFile.id)) || []
    );
  }, [lmBatchFiles]);

  const selectedCountOnPage = validBatchIds.filter((id) => selectedBatchIds.includes(id)).length;
  const isAllSelectedOnPage = validBatchIds.length > 0 && selectedCountOnPage === validBatchIds.length;
  const isIndeterminateOnPage = selectedCountOnPage > 0 && selectedCountOnPage < validBatchIds.length;
  const isSubmitting = isSubmittingSendBack || isSubmittingReject || isSubmittingApprove;
  const isReasonDialogSubmitting = isSubmittingSendBack || isSubmittingReject;
  const batchActionTitles: Record<BatchAction, string> = {
    sendBack: t('opm_pd.sendBack'),
    reject: t('opm_pd.reject'),
    approve: t('opm_pd.approve'),
  };
  const batchActionConfirmMessages: Record<BatchAction, string> = {
    sendBack: t('opm_pd.confirmSendBackBatchFilesContentText'),
    reject: t('opm_pd.confirmRejectBatchFilesContentText'),
    approve: t('opm_pd.confirmApproveBatchFilesContentText'),
  };
  const batchActionSuccessMessages: Record<BatchAction, string> = {
    sendBack: t('opm_pd.sendBackBatchFilesSuccessfully'),
    reject: t('opm_pd.rejectBatchFilesSuccessfully'),
    approve: t('opm_pd.approveBatchFilesSuccessfully'),
  };
  const reasonDialogTitle = reasonDialogAction ? batchActionTitles[reasonDialogAction] : '';

  const handleSelectAllOnPage = (checked: boolean) => {
    setSelectedBatchIds((prev) => {
      if (checked) {
        return Array.from(new Set([...prev, ...validBatchIds]));
      }

      const batchIdSet = new Set(validBatchIds);
      return prev.filter((id) => !batchIdSet.has(id));
    });
  };

  const handleToggleBatch = (batchId: string) => {
    setSelectedBatchIds((prev) => {
      if (prev.includes(batchId)) {
        return prev.filter((id) => id !== batchId);
      }

      return [...prev, batchId];
    });
  };

  const showSuccessAlert = (content: string) => {
    dispatch(
      appendAlertItem([
        {
          severity: AlertType.SUCCESS,
          title: t('opm_pd.success'),
          content,
        },
      ]),
    );
  };

  const confirmBatchAction = async (action: BatchAction) => {
    return alertDialog.fire({
      title: t('opm_pd.confirm'),
      description: batchActionConfirmMessages[action],
    });
  };

  const handleApproveBatchFiles = async () => {
    const batchIds = [...selectedBatchIds];
    if (!batchIds.length || isSubmittingApprove) return;

    const { isConfirmed } = await confirmBatchAction('approve');
    if (!isConfirmed) return;

    setIsSubmittingApprove(true);
    try {
      await approveLmBatchFiles({ batchIds }, dispatch);
      setSelectedBatchIds([]);
      refreshData();
      showSuccessAlert(batchActionSuccessMessages.approve);
    } catch (error) {
    } finally {
      setIsSubmittingApprove(false);
    }
  };

  const handleOpenReasonDialog = (action: ReasonDialogAction) => {
    if (!selectedBatchIds.length || isSubmitting) return;
    setReasonDialogAction(action);
  };

  const handleCloseReasonDialog = () => {
    if (isReasonDialogSubmitting) return;
    setReasonDialogAction(null);
  };

  const handleSubmitReason = async (reason: string) => {
    if (!reasonDialogAction || isReasonDialogSubmitting) return;

    const action = reasonDialogAction;
    const batchIds = [...selectedBatchIds];
    if (!batchIds.length) return;

    const { isConfirmed } = await confirmBatchAction(action);
    if (!isConfirmed) return;

    if (action === 'sendBack') {
      setIsSubmittingSendBack(true);
      try {
        await sendBackLmBatchFiles({ batchIds, reason: reason.trim() }, dispatch);
        setSelectedBatchIds([]);
        refreshData();
        showSuccessAlert(batchActionSuccessMessages.sendBack);
        setReasonDialogAction(null);
      } catch (error) {
      } finally {
        setIsSubmittingSendBack(false);
      }
      return;
    }

    setIsSubmittingReject(true);
    try {
      await rejectLmBatchFiles({ batchIds, reason: reason.trim() }, dispatch);
      setSelectedBatchIds([]);
      refreshData();
      showSuccessAlert(batchActionSuccessMessages.reject);
      setReasonDialogAction(null);
    } catch (error) {
    } finally {
      setIsSubmittingReject(false);
    }
  };

  const batchFileIds = useMemo(() => {
    return lmBatchFiles?.items?.map((batchFile) => String(batchFile.id)) || [];
  }, [lmBatchFiles?.items]);
  const batchFileIdsRef = useRef(batchFileIds);

  useEffect(() => {
    if (_isEqual(batchFileIdsRef.current, batchFileIds)) {
      return;
    }
    setSelectedBatchIds([]);
    batchFileIdsRef.current = batchFileIds;
  }, [batchFileIds]);

  return (
    <>
      <AppTable
        title={`${t('opm_pd.batchFileList')} ${
          selectedBatchIds.length ? `(${t('opm_pd.batchSelected', { count: selectedBatchIds.length })})` : ''
        }`}
        disableBulkSelect
        operationDef={[]}
        startHeaderActions={
          <>
            <Button
              startIcon={isSubmittingSendBack ? <CircularProgress size={20} /> : null}
              variant="contained"
              color="error"
              onClick={() => handleOpenReasonDialog('sendBack')}
              disabled={isSubmitting || selectedBatchIds.length === 0}
            >
              {t('opm_pd.sendBack')}
            </Button>
            <Button
              startIcon={isSubmittingReject ? <CircularProgress size={20} /> : null}
              variant="contained"
              color="error"
              onClick={() => handleOpenReasonDialog('reject')}
              disabled={isSubmitting || selectedBatchIds.length === 0}
            >
              {t('opm_pd.reject')}
            </Button>
            <Button
              startIcon={isSubmittingApprove ? <CircularProgress size={20} /> : null}
              variant="contained"
              color="secondary"
              onClick={handleApproveBatchFiles}
              disabled={isSubmitting || selectedBatchIds.length === 0}
            >
              {t('opm_pd.approve')}
            </Button>
          </>
        }
        columnDef={[
          {
            isId: true,
            hidden: true,
            keyIndex: 'id',
            displayName: '',
            renderData: () => '',
            sortable: true,
            onSort: (sort) => {
              onSort({ key: '', value: sort[''] });
            },
          },
          {
            isId: false,
            hidden: false,
            keyIndex: 'policyId',
            displayName: (
              <>
                <Checkbox
                  checked={isAllSelectedOnPage}
                  indeterminate={isIndeterminateOnPage}
                  onChange={(event) => handleSelectAllOnPage(event.target.checked)}
                  sx={{
                    color: 'white',
                    '&.Mui-checked': { color: 'white' },
                    '&.MuiCheckbox-indeterminate': { color: 'white' },
                  }}
                />
              </>
            ),
            renderData: (row) => {
              const batchId = String(row.id ?? '');
              const disabled = (row.awaitingApprovalCount || 0) < 1;

              return (
                <>
                  <Checkbox
                    checked={selectedBatchIds.includes(batchId)}
                    onClick={() => handleToggleBatch(batchId)}
                    disabled={disabled}
                  />
                </>
              );
            },
            padding: 'checkbox',
            sortable: false,
          },
          {
            isId: false,
            hidden: false,
            keyIndex: 'batchNo',
            displayName: t('opm_pd.batchNo'),
            renderData: (row) => {
              return (
                <Typography variant="inherit" noWrap>
                  {row.batchNo}
                </Typography>
              );
            },
            sortable: false,
            onSort: (sort) => {
              onSort({ key: '', value: sort[''] });
            },
          },
          {
            isId: false,
            hidden: false,
            keyIndex: 'submittedAt',
            displayName: t('opm_pd.submittedDate'),
            renderData: (row) => (row.submittedAt ? formatDateTime(row.submittedAt, 'DD/MM/YYYY') : ''),
            sortable: false,
          },
          {
            isId: false,
            hidden: false,
            keyIndex: 'submittedName',
            displayName: t('opm_pd.submittedBy'),
            renderData: (row) => row.submittedName,
            sortable: false,
          },
          {
            isId: false,
            hidden: false,
            keyIndex: 'submittedPoliciesCount',
            displayName: t('opm_pd.submittedPolicies'),
            renderData: (row) => formatNumber(row.submittedPoliciesCount),
            align: 'right',
            sortable: false,
          },
          {
            isId: false,
            hidden: false,
            keyIndex: 'awaitingApprovalCount',
            displayName: t('opm_pd.awaitingApprovalLm'),
            renderData: (row) => formatNumber(row.awaitingApprovalCount),
            align: 'right',
            sortable: false,
          },
          {
            isId: false,
            hidden: false,
            keyIndex: 'actions',
            displayName: t('opm_pd.operations'),
            renderData: (row) => {
              return (
                <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
                  <Button
                    size="small"
                    variant="outlined"
                    color="secondary"
                    component={Link}
                    to={`/opm-pd/request-approval-to-line-manager/batch-files/${row.id}/policies-approval-request`}
                  >
                    {t('opm_pd.view')}
                  </Button>
                </Box>
              );
            },
            align: 'right',
            sortable: false,
          },
        ]}
        isLoading={isLoading}
        onRefresh={refreshData}
        dataSource={lmBatchFiles?.items || []}
        totalPages={lmBatchFiles?.totalPages}
        totalRecords={lmBatchFiles?.total}
        onChangePage={(page, rowsPerPage) => {
          setFilterState({ ...filterState, page, limit: rowsPerPage });
          refreshData();
        }}
      />
      <OpmPdReasonFormDialog
        open={!!reasonDialogAction}
        title={reasonDialogTitle}
        isSubmitting={isReasonDialogSubmitting}
        onClose={handleCloseReasonDialog}
        onSubmit={handleSubmitReason}
      />
    </>
  );
};

export default BatchFileList;
